import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PodcastEpisodeGuest } from '@/types/podcastHost';
import { toast } from 'sonner';

export const usePodcastEpisodeGuests = (episodeId?: string) => {
  return useQuery({
    queryKey: ['podcast-episode-guests', episodeId],
    queryFn: async () => {
      if (!episodeId) return [];
      
      const { data, error } = await supabase
        .from('os_podcast_shows_episode_guests')
        .select(`
          *,
          guest:os_podcast_shows_guests(id, name, company, job_title, image_url)
        `)
        .eq('episode_id', episodeId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as unknown as PodcastEpisodeGuest[];
    },
    enabled: !!episodeId,
  });
};

export const useAddEpisodeGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      episodeId, 
      guestId, 
      brandId,
      role = 'co_guest',
      status = 'confirmed'
    }: { 
      episodeId: string; 
      guestId: string; 
      brandId: string;
      role?: 'main_guest' | 'co_guest' | 'panelist';
      status?: 'invited' | 'confirmed' | 'declined' | 'appeared';
    }) => {
      const { data, error } = await supabase
        .from('os_podcast_shows_episode_guests')
        .insert({
          episode_id: episodeId,
          guest_id: guestId,
          brand_id: brandId,
          role,
          status,
        })
        .select(`
          *,
          guest:os_podcast_shows_guests(id, name, company, job_title, image_url)
        `)
        .single();

      if (error) throw error;
      return data as unknown as PodcastEpisodeGuest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-episode-guests', data.episode_id] });
      toast.success('Guest added to episode');
    },
    onError: (error: any) => {
      console.error('Error adding guest to episode:', error);
      if (error.code === '23505') {
        toast.error('This guest is already added to the episode');
      } else {
        toast.error('Failed to add guest');
      }
    },
  });
};

export const useUpdateEpisodeGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      id, 
      episodeId,
      updates 
    }: { 
      id: string; 
      episodeId: string;
      updates: Partial<Pick<PodcastEpisodeGuest, 'role' | 'status' | 'invitation_sent_at' | 'confirmed_at'>>;
    }) => {
      const { data, error } = await supabase
        .from('os_podcast_shows_episode_guests')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { ...data, episodeId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-episode-guests', data.episodeId] });
      toast.success('Guest updated');
    },
    onError: (error) => {
      console.error('Error updating episode guest:', error);
      toast.error('Failed to update guest');
    },
  });
};

export const useRemoveEpisodeGuest = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, episodeId }: { id: string; episodeId: string }) => {
      const { error } = await supabase
        .from('os_podcast_shows_episode_guests')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return episodeId;
    },
    onSuccess: (episodeId) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-episode-guests', episodeId] });
      toast.success('Guest removed from episode');
    },
    onError: (error) => {
      console.error('Error removing guest from episode:', error);
      toast.error('Failed to remove guest');
    },
  });
};
